import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Search, Plus, Eye, MoreHorizontal, Edit, Trash2, Users, MapPin } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { getProjects, createProject, updateProject, deleteProject, getEmployees } from '../../services/api';
import { nextOwnStatus } from '../../lib/progress';
import MaterialUsagePanel from '../../components/inventory/MaterialUsagePanel';


export const ProjectsPage = () => {
  const { user, can } = useAuth();
  const canCreate = can('projects.create');
  const canEditAll = can('projects.edit', 'all');
  const canEditOwn = can('projects.edit');
  const canAssign = can('projects.assign');
  const canDelete = can('projects.delete');
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    address: '',
    description: '',
    work_type: 'installation',
    material_details: '',
    start_date: '',
    assigned_employees: []
  });


  const fetchData = useCallback(async () => {
    try {
      const [projectsRes, employeesRes] = await Promise.all([
        getProjects(),
        canAssign ? getEmployees() : Promise.resolve({ data: [] })
      ]);
      setProjects(projectsRes.data);
      setEmployees(employeesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [canAssign]);


  useEffect(() => {
    fetchData();
  }, [fetchData]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode && selectedProject) {
        const payload = { ...formData };
        if (!canAssign) delete payload.assigned_employees;
        await updateProject(selectedProject.id, payload);
        toast.success('Chantier mis à jour');
      } else {
        await createProject(formData);
        toast.success('Chantier créé');
      }
      setFormOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };


  const handleDelete = async (projectId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce chantier?')) return;
    try {
      await deleteProject(projectId);
      toast.success('Chantier supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };


  const handleStatusUpdate = async (projectId, newStatus) => {
    try {
      await updateProject(projectId, { status: newStatus });
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };


  const resetForm = () => {
    setFormData({
      client_name: '',
      client_email: '',
      client_phone: '',
      address: '',
      description: '',
      work_type: 'installation',
      material_details: '',
      start_date: '',
      assigned_employees: []
    });
    setEditMode(false);
    setSelectedProject(null);
  };


  const openEditForm = (project) => {
    setFormData({
      client_name: project.client_name,
      client_email: project.client_email || '',
      client_phone: project.client_phone || '',
      address: project.address,
      description: project.description,
      work_type: project.work_type,
      material_details: project.material_details || '',
      start_date: project.start_date || '',
      assigned_employees: project.assigned_employees || []
    });
    setSelectedProject(project);
    setEditMode(true);
    setFormOpen(true);
  };


  const toggleEmployee = (employeeId) => {
    setFormData(prev => {
      const current = prev.assigned_employees || [];
      const updated = current.includes(employeeId)
        ? current.filter(id => id !== employeeId)
        : [...current, employeeId];
      return { ...prev, assigned_employees: updated };
    });
  };


  const filteredProjects = projects.filter(project => {
    const matchesSearch = 
      project.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.address?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    return matchesSearch && matchesStatus;
  });


  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-pending',
      assigned: 'badge-confirmed',
      in_progress: 'badge-in-progress',
      completed: 'badge-completed'
    };
    return badges[status] || 'badge-pending';
  };


  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      assigned: 'Assigné',
      in_progress: 'En cours',
      completed: 'Terminé'
    };
    return labels[status] || status;
  };


  const getWorkTypeLabel = (type) => {
    const labels = {
      installation: 'Installation',
      repair: 'Réparation',
      maintenance: 'Entretien'
    };
    return labels[type] || type;
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#00b4d8] border-t-transparent rounded-full"></div>
      </div>
    );
  }


  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1a3a5c]">Chantiers</h1>
          <p className="text-slate-500">Gérez vos projets et chantiers</p>
        </div>
        {canCreate && (
          <Button 
            onClick={() => {
              resetForm();
              setFormOpen(true);
            }}
            className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8]"
            data-testid="add-project-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau chantier
          </Button>
        )}
      </div>


      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Rechercher par client ou adresse..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="assigned">Assigné</SelectItem>
                <SelectItem value="in_progress">En cours</SelectItem>
                <SelectItem value="completed">Terminé</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>


      {/* Projects Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProjects.map((project) => (
          <Card key={project.id} className="card-hover" data-testid={`project-card-${project.id}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-[#1a3a5c]">{project.client_name}</h3>
                  <p className="text-sm text-slate-500">{getWorkTypeLabel(project.work_type)}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(project.status)}`}>
                  {getStatusLabel(project.status)}
                </span>
              </div>


              <p className="text-sm text-slate-500 flex items-center gap-1 mb-2">
                <MapPin className="w-4 h-4" />
                {project.address}
              </p>


              <p className="text-sm text-slate-600 line-clamp-2 mb-4">
                {project.description}
              </p>


              {project.assigned_employees?.length > 0 && (
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-500">
                    {project.assigned_employees.length} employé(s)
                  </span>
                </div>
              )}


              <div className="flex items-center gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedProject(project);
                    setDetailsOpen(true);
                  }}
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Voir
                </Button>
                {!canEditAll && canEditOwn && (project.assigned_employees || []).includes(user?.id) && nextOwnStatus(project.status) && (
                  <Button
                    size="sm"
                    className="bg-[#00b4d8] hover:bg-[#009bb8]"
                    onClick={() => handleStatusUpdate(project.id, nextOwnStatus(project.status).value)}
                    data-testid={`progress-project-${project.id}`}
                  >
                    {nextOwnStatus(project.status).label}
                  </Button>
                )}
                {(canEditAll || canDelete) && (
                  <>
                    {canEditAll && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditForm(project)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {canEditAll && (<>
                        <DropdownMenuItem onClick={() => handleStatusUpdate(project.id, 'assigned')}>
                          Assigné
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleStatusUpdate(project.id, 'in_progress')}>
                          En cours
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleStatusUpdate(project.id, 'completed')}>
                          Terminé
                        </DropdownMenuItem>
                        </>)}
                        {canDelete && (
                        <DropdownMenuItem 
                          onClick={() => handleDelete(project.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Supprimer
                        </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>


      {filteredProjects.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <p>Aucun chantier trouvé</p>
        </div>
      )}


      {/* Form Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Modifier le chantier' : 'Nouveau chantier'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nom du client *</Label>
              <Input
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Courriel</Label>
                <Input
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={formData.client_phone}
                  onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Adresse *</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Type de travaux</Label>
              <Select 
                value={formData.work_type} 
                onValueChange={(value) => setFormData({ ...formData, work_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="installation">Installation</SelectItem>
                  <SelectItem value="repair">Réparation</SelectItem>
                  <SelectItem value="maintenance">Entretien</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Détails du matériel</Label>
              <Textarea
                value={formData.material_details}
                onChange={(e) => setFormData({ ...formData, material_details: e.target.value })}
                placeholder="Couleur des caches, longueur de tuyaux, PEX ou cuivre..."
              />
            </div>
            <div>
              <Label>Date de début</Label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              />
            </div>


            {canAssign && employees.length > 0 && (
              <div>
                <Label>Assigner des employés</Label>
                <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border rounded-lg p-3">
                  {employees.map((emp) => {
                    const isSelected = (formData.assigned_employees || []).includes(emp.id);
                    return (
                      <button
                        type="button"
                        key={emp.id} 
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors w-full text-left ${
                          isSelected 
                            ? 'bg-[#00b4d8] text-white' 
                            : 'hover:bg-slate-100'
                        }`}
                        onClick={() => toggleEmployee(emp.id)}
                      >
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                          isSelected 
                            ? 'bg-white border-white' 
                            : 'border-slate-300'
                        }`}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-[#00b4d8]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className="text-sm flex-1">
                          {emp.first_name} {emp.last_name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}


            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#00b4d8] hover:bg-[#009bb8]">
                {editMode ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>


      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Détails du chantier</DialogTitle>
          </DialogHeader>
          {selectedProject && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Client</p>
                  <p className="font-medium">{selectedProject.client_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Type</p>
                  <p className="font-medium">{getWorkTypeLabel(selectedProject.work_type)}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-slate-500">Adresse</p>
                <p className="font-medium">{selectedProject.address}</p>
              </div>


              <div>
                <p className="text-sm text-slate-500">Description</p>
                <p className="font-medium bg-slate-50 p-3 rounded-lg">{selectedProject.description}</p>
              </div>


              {selectedProject.material_details && (
                <div>
                  <p className="text-sm text-slate-500">Notes de matériel antérieures</p>
                  <p className="font-medium">{selectedProject.material_details}</p>
                </div>
              )}


              {selectedProject.start_date && (
                <div>
                  <p className="text-sm text-slate-500">Date de début</p>
                  <p className="font-medium">{selectedProject.start_date}</p>
                </div>
              )}


              {/* Employés assignés */}
              <div>
                <p className="text-sm text-slate-500 mb-2">Employés assignés</p>
                {selectedProject.assigned_employees?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.assigned_employees.map(empId => {
                      const emp = employees.find(e => e.id === empId);
                      return emp ? (
                        <span key={empId} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#00b4d8]/10 text-[#00b4d8] text-sm font-medium">
                          <Users className="w-3 h-3" />
                          {emp.first_name} {emp.last_name}
                        </span>
                      ) : null;
                    })}
                  </div>
                ) : (
                  <p className="text-slate-400 italic">Aucun employé assigné</p>
                )}
              </div>


              <MaterialUsagePanel
                parentType="project"
                parentId={selectedProject.id}
                user={user}
              />

              {canEditAll && (
                <div className="pt-4 border-t">
                  <Button 
                    onClick={() => {
                      openEditForm(selectedProject);
                      setDetailsOpen(false);
                    }}
                    className="w-full bg-[#00b4d8] hover:bg-[#009bb8]"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    {canAssign ? 'Modifier / Assigner des employés' : 'Modifier'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default ProjectsPage;
